import React, { useState, useEffect, useCallback } from 'react';
import { useLoader } from '../../../loader/LoaderContext';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import userApiService from '../../../services/oraganisation-api.service';
import { useNavigate, useOutletContext } from 'react-router';
import UserManagement from './UserManagment';
import PageLoader from '../../../components/PageLoader/PageLoader';
import { delay } from '../../../utils/helpers';

function UserManagementContainer() {
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const { organizationData, isSuperAdmin, orgId, refreshOrganizationData } =
    useOutletContext();

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPaginatedUsers, setTotalPaginatedUsers] = useState(0);

  // Unified loading state
  const [isPageReady, setIsPageReady] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const loggedInUserAttributes = useSelector(
    (state) => state.auth.userAttributes,
  );

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  const [loadingStates, setLoadingStates] = useState({
    initialLoad: true,
    search: false,
    delete: false,
    create: false,
    edit: false,
    statusChange: false,
  });

  const [modalStates, setModalStates] = useState({
    userModal: false,
    deleteModal: false,
    statusModal: false,
  });

  const [deleteUserId, setDeleteUserId] = useState(null);
  const [statusChangeUser, setStatusChangeUser] = useState(null);

  // Slots configuration
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);

  const isSubForceAllowed = organizationData?.sub_force_allow === true;
  const isFreeTrialOrg = organizationData?.status === 'free_trial';
  // For free trial: 1 seat (no Stripe product yet). For paid: sum seats_providers + seats_support from DynamoDB.
  const baseUsers = isFreeTrialOrg
    ? 1
    : (parseInt(organizationData?.seats_providers, 10) || 0) + (parseInt(organizationData?.seats_support, 10) || 0);
  const slotsInfo = {
    isFreeTrialOrg,
    baseUsers,
    currentUsers: totalUsers,
  };

  const fetchUsers = useCallback(
    async (search = '', page = 1, pageLimit = limit) => {
      setLoadingStates((prev) => ({ ...prev, search: true }));
      try {
        const response = await userApiService.listOrganizationUsers({
          limit: pageLimit,
          searchTerm: search,
          pageNumber: page,
          organizationID: isSuperAdmin
            ? organizationData?.organization_id
            : null,
        });

        // Transform users to include has_admin_access field based on role
        const transformedUsers = response.results.map(user => ({
          ...user,
          has_admin_access: user.role === 'org_admin'
        }));

        setUsers(transformedUsers);
        setTotalUsers(response.total_users);
        setTotalPaginatedUsers(response.total_users);
        setTotalPages(Math.ceil(response.total_users / pageLimit));
      } catch (error) {
        toast.error('Error while fetching users');
      } finally {
        setLoadingStates((prev) => ({
          ...prev,
          search: false,
          initialLoad: false,
        }));
      }
    },
    [limit, showLoader, hideLoader, isSuperAdmin, organizationData],
  );

  // Fetch role types
  const fetchRoleTypes = async () => {
    const roleTypes = await userApiService.getUserRoleTypes();
    setRoles(roleTypes.user_types_list);
  };

  // Unified data loading - load ALL required data before marking page ready
  useEffect(() => {
    if (!organizationData) {
      setIsPageReady(false);
      return;
    }

    const loadAllData = async () => {
      setIsPageReady(false);
      setLoadError(null);

      try {
        // Set subscription details (synchronous)
        setSubscriptionDetails(null);
        setTotalUsers(0);

        // Fetch users and roles in parallel
        await Promise.all([
          fetchUsers(searchTerm, currentPage, limit),
          fetchRoleTypes()
        ]);

        // Mark page as ready when ALL data is loaded
        setIsPageReady(true);
      } catch (error) {
        console.error('Error loading user management data:', error);
        setLoadError('Failed to load user management data');
        setIsPageReady(false);
      }
    };

    loadAllData();
  }, [organizationData]);

  // Handle pagination changes (reload users only)
  useEffect(() => {
    if (isPageReady) {
      fetchUsers(searchTerm, currentPage, limit);
    }
  }, [currentPage, limit]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value);
    setLimit(newLimit);
    setCurrentPage(1); // Reset to first page when changing limit
  };

  const handleModalToggle = (modalType, value) => {
    setModalStates((prev) => ({ ...prev, [modalType]: value }));
    if (!value) {
      setDeleteUserId(null);
      setStatusChangeUser(null);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleCreateUser = async (values, { setSubmitting, resetForm }) => {
    showLoader();
    setLoadingStates((prev) => ({ ...prev, create: true }));
    try {
      await userApiService.createOrganizationUser(
        {
          name: `${values.firstName} ${values.lastName}`,
          email: values.email,
          user_name: values.email,
          user_type: values.role,
          is_administrative_access: values.hasAdminAccess,
          npi_number: values.selectedNpi?.npi ?? '',
        },
        organizationData?.user_name,
      );

      await delay(2000);

      if (isSuperAdmin) {
        hideLoader();
        await refreshOrganizationData();
        handleModalToggle('userModal', false);
        toast.success('User created successfully.');
      } else {
        await refreshOrganizationData();
        await fetchUsers(searchTerm, currentPage, limit);
        hideLoader();
        handleModalToggle('userModal', false);
        toast.success('User created successfully.');
      }

      resetForm();
    } catch (error) {
      const errorMessage = JSON.parse(error.response.body);
      toast.error(errorMessage.error);
    } finally {
      hideLoader();
      setLoadingStates((prev) => ({ ...prev, create: false }));
      setSubmitting(false);
    }
  };

  const handleEditClick = (user) => {
    const [firstName, ...lastNameParts] = user.name.split(' ');
    const lastName = lastNameParts.join(' ');

    setEditingUser({
      userName: user.user_name,
      firstName,
      lastName,
      email: user.email,
      role: user?.user_type_key,
      orgNpiNumber: user?.npi_number,
      selectedNpi: { npi: user?.npi_number },
      hasAdminAccess: user.role === 'org_admin',
    });

    handleModalToggle('userModal', true);
  };

  const handleUpdateUser = async (values, { setSubmitting }) => {
    showLoader();
    setLoadingStates((prev) => ({ ...prev, edit: true }));
    try {
      await userApiService.updateOrganisationDetails(
        editingUser.userName,
        {
          name: `${values.firstName} ${values.lastName}`,
          email: values.email,
          user_type: values.role,
          is_administrative_access: values.hasAdminAccess,
          npi_number: values.selectedNpi?.npi ?? '',
        },
        organizationData?.user_name,
      );
      await delay(2000);

      if (isSuperAdmin) {
        hideLoader();
        await refreshOrganizationData();
        handleModalToggle('userModal', false);
        toast.success('User updated successfully.');
      } else {
        await refreshOrganizationData();
        await fetchUsers(searchTerm, currentPage, limit);
        hideLoader();
        handleModalToggle('userModal', false);
        toast.success('User updated successfully.');
      }
    } catch (error) {
      const errorMessage = error.response?.body
        ? JSON.parse(error.response.body).error
        : `Error while updating user`;

      toast.error(errorMessage);
    } finally {
      hideLoader();
      handleModalToggle('userModal', false);
      setLoadingStates((prev) => ({ ...prev, edit: false }));
      setSubmitting(false);
      setEditingUser(null);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserId) return;
    showLoader();

    setLoadingStates((prev) => ({ ...prev, delete: true }));
    try {
      await userApiService.deleteOrganizationUser(
        deleteUserId,
        organizationData?.user_name,
      );

      // If this is the last item on the last page, go to previous page
      const isLastItemOnPage = users.length === 1;
      const isLastPage = currentPage === totalPages;

      await delay(2000);

      if (isLastItemOnPage && isLastPage && currentPage > 1) {
        setCurrentPage((prev) => prev - 1);
      } else {
        if (!isSuperAdmin) {
          await fetchUsers(searchTerm, currentPage, limit);
        }
      }

      if (isSuperAdmin) {
        hideLoader();
        await refreshOrganizationData();
        handleModalToggle('deleteModal', false);
        toast.success('User deleted successfully.');
      } else {
        await refreshOrganizationData();
        await fetchUsers(searchTerm, currentPage, limit);
        hideLoader();
        handleModalToggle('deleteModal', false);
        toast.success('User deleted successfully.');
      }
    } catch (error) {
      const errorMessage = error.response?.body
        ? JSON.parse(error.response.body).error
        : `Error while deleting user`;

      toast.error(errorMessage);
    } finally {
      hideLoader();
      setLoadingStates((prev) => ({ ...prev, delete: false }));
    }
  };

  const handleStatusChange = async () => {
    if (!statusChangeUser) return;

    showLoader();
    setLoadingStates((prev) => ({ ...prev, statusChange: true }));

    try {
      await userApiService.updateStatusOfOrganizationUser(
        statusChangeUser.user_name,
        statusChangeUser.enabled,
        organizationData?.user_name,
      );

      const statusAction = statusChangeUser.enabled
        ? 'deactivated'
        : 'activated';

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.user_name === statusChangeUser.user_name
            ? {
                ...user,
                enabled: !user.enabled,
              }
            : user,
        ),
      );

      if (isSuperAdmin) {
        await delay(2000);
        hideLoader();
        await refreshOrganizationData();
        toast.success(`User ${statusAction} successfully.`);
      } else {
        await refreshOrganizationData();
        await delay(2000);
        hideLoader();
        toast.success(`User ${statusAction} successfully.`);
      }
    } catch (error) {
      const errorMessage = error.response?.body
        ? JSON.parse(error.response.body).error
        : `Error while ${statusChangeUser.enabled ? 'deactivating' : 'activating'} user`;

      toast.error(errorMessage);
    } finally {
      hideLoader();
      handleModalToggle('statusModal', false);
      setLoadingStates((prev) => ({ ...prev, statusChange: false }));
      setStatusChangeUser(null);
    }
  };

  const handleUpgradePlan = () => {
    if (isSuperAdmin && orgId) {
      navigate(`/super-admin/${orgId}/change-plan`, {
        state: { organization: organizationData, isSuperAdmin },
      });
    } else {
      navigate('/settings/change-plan', {
        state: { organization: organizationData },
      });
    }
  };

  // Show error state if data failed to load
  if (loadError) {
    return (
      <div className="user-management-container">
        <div className="error-state" style={{ 
          padding: '40px', 
          textAlign: 'center',
          background: 'var(--card-bg, #ffffff)',
          border: '1px solid var(--border-grey, #e5e7eb)',
          borderRadius: '8px'
        }}>
          <p style={{ color: 'var(--error-color, #dc2626)', marginBottom: '20px' }}>
            {loadError}
          </p>
          <button 
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show unified loading state until ALL data is ready
  if (!isPageReady) {
    return <PageLoader message="Loading user management..." />;
  }

  return (
    <UserManagement
      isSuperAdmin={isSuperAdmin}
      users={users}
      totalPaginatedUsers={totalPaginatedUsers}
      totalUsers={totalUsers}
      limit={limit}
      roles={roles}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      editingUser={editingUser}
      setEditingUser={setEditingUser}
      currentPage={currentPage}
      totalPages={totalPages}
      loadingStates={loadingStates}
      modalStates={modalStates}
      deleteUserId={deleteUserId}
      statusChangeUser={statusChangeUser}
      loggedInUserAttributes={loggedInUserAttributes}
      slotsInfo={slotsInfo}
      handlePageChange={handlePageChange}
      handleLimitChange={handleLimitChange}
      handleModalToggle={handleModalToggle}
      handleClearSearch={handleClearSearch}
      handleCreateUser={handleCreateUser}
      handleEditClick={handleEditClick}
      handleUpdateUser={handleUpdateUser}
      handleDeleteUser={handleDeleteUser}
      handleStatusChange={handleStatusChange}
      setDeleteUserId={setDeleteUserId}
      setStatusChangeUser={setStatusChangeUser}
      handleUpgradePlan={handleUpgradePlan}
      subscriptionDetails={subscriptionDetails}
      isSubForceAllowed={isSubForceAllowed}
    />
  );
}

export default UserManagementContainer;
